import { useDispatch, useSelector } from "react-redux"
import { ApplicationState } from "../../interface/ApplicationState";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import Slider from "@mui/material/Slider";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Divider from "@mui/material/Divider";
import { User } from "../../interface/User";
import { setBacktestConfig } from "../../redux/reducers/backtesting/backtestingReducer";
import { updateLocalUser as updateLocalUserData } from "../../redux/actions/userActions";
import { SmartMoneyConfig } from "../../interface/SmartMoney";

interface SmartMoneyConfigProps
{
  user: User
  isBacktest?: boolean
}

export const SmartMoneySection = ({ user, isBacktest }: SmartMoneyConfigProps) => {

  const dispatch = useDispatch();
  const updateLocalUser = isBacktest ? setBacktestConfig : updateLocalUserData

  const handleToggle = (enabled: boolean) => {

    if(enabled)
    {
      const config: SmartMoneyConfig = {
        isSmartMoneyEnabled: true,
        fastEma: 9,
        slowEma: 21,
        rsiPeriod: 14,
        atrPeriod: 14,
        macdFast: 12,
        macdSlow: 26,
        macdSignal: 9,
        swingLookback: 5,
        atrStop: 1.5,
        enableRsi: false,
        atrTake: 2,
        rsiBuy: 30,
        rsiSell: 60,
        isStopLossEnabled: true,
        isTakeProfitEnabled: true,
      }

      dispatch(updateLocalUser({ smartMoney: config }))
    }
    else
    {
      dispatch(updateLocalUser({ smartMoney: { ...user.smartMoney, isSmartMoneyEnabled: false } }))
    }

  }

  return (
   <Accordion >
   <AccordionSummary
     
     expandIcon={<ExpandMoreIcon />}
     aria-controls="panel1a-content"
     id="panel1a-header"
   >

   <FormControlLabel
     control={<Switch checked={user?.smartMoney?.isSmartMoneyEnabled} onChange={(e, checked) => handleToggle(checked)} name="Enable Smart Money Strategy" />}
     label="Enable Smart Money Strategy" />

   </AccordionSummary>
   <AccordionDetails>

   <Typography variant="caption" color="textSecondary">EMA Config {user.smartMoney?.fastEma}/{user.smartMoney?.slowEma}</Typography>

     <Slider
      marks
      max={100}
      disableSwap
      sx={{ mt: 6 }}
      valueLabelFormat={(i, b) => `${i} EMA`}
      value={[user.smartMoney?.slowEma || 9, user.smartMoney?.fastEma || 21]}
      onChange={(e: any) => dispatch(updateLocalUser({ smartMoney: { ...user.smartMoney, fastEma: e.target.value[0], slowEma: e.target.value[1] } }))}
      valueLabelDisplay="on"
     />

    <Divider />


    <FormControlLabel
     sx={{ mt: 1, mb: 1 }} 
     control={<Switch checked={user?.smartMoney?.enableRsi} onChange={(e, checked) => dispatch(updateLocalUser({ smartMoney: { ...user.smartMoney, enableRsi: checked } }))} name="Enable RSI" />}
     label={`Enable RSI Config - Buy When RSI is below or equal to ${user.smartMoney?.rsiBuy} and sell when RSI is above or equal to ${user.smartMoney?.rsiSell}`} />

      { user?.smartMoney?.enableRsi && <><Slider
        marks
        max={100}
        disableSwap
        sx={{ mt: 6 }}
        valueLabelFormat={(i, b) => `${i} RSI`}
        value={[user.smartMoney?.rsiBuy || 30, user.smartMoney?.rsiSell || 60]}
        onChange={(e: any) => dispatch(updateLocalUser({ smartMoney: { ...user.smartMoney, rsiBuy: e.target.value[0], rsiSell: e.target.value[1] } }))}
        valueLabelDisplay="on"
        />

      <Typography variant="caption" color="textSecondary">RSI Period</Typography>

      <Slider
        marks
        max={50}
        sx={{ mt: 6 }}
        valueLabelFormat={(i, b) => `${i}`}
        value={user.smartMoney?.rsiPeriod || 14}
        onChange={(e: any) => dispatch(updateLocalUser({ smartMoney: { ...user.smartMoney, rsiPeriod: e.target.value } }))}
        valueLabelDisplay="on"
      /> </> }


    <Divider /> 

    <Typography variant="caption" color="textSecondary">ATR Period</Typography>

    <Slider
      marks
      max={50}
      sx={{ mt: 6 }}
      valueLabelFormat={(i, b) => `${i}`}
      value={user.smartMoney?.atrPeriod || 14}
      onChange={(e: any) => dispatch(updateLocalUser({ smartMoney: { ...user.smartMoney, atrPeriod: e.target.value } }))}
      valueLabelDisplay="on"
    />


    <Divider />

    <Typography variant="caption" color="textSecondary">MACD Config: Signal: {user.smartMoney?.macdSignal} | Fast: {user.smartMoney?.macdFast} | Slow: {user.smartMoney?.macdSlow}</Typography>

     <Slider
      max={50}
      marks
      disableSwap
      sx={{ mt: 6 }}
      value={[user.smartMoney?.macdSignal || 9, user.smartMoney?.macdFast || 12, user.smartMoney?.macdSlow || 26]}
      onChange={(e: any) => dispatch(updateLocalUser({ smartMoney: { ...user.smartMoney, macdSignal: e.target.value[0], macdFast: e.target.value[1], macdSlow: e.target.value[2] } }))}
      valueLabelDisplay="on"
     />

    <Divider />

    <Typography variant="caption" color="textSecondary">Swing lookback</Typography>

    <Slider
      marks
      valueLabelFormat={(a,b) => `${a} candles`}
      sx={{ mt: 6 }}
      value={user.smartMoney?.swingLookback || 5}
      onChange={(e: any) => dispatch(updateLocalUser({ smartMoney: { ...user.smartMoney, swingLookback: e.target.value } }))}
      valueLabelDisplay="on"
    />

    <Divider />

    <FormControlLabel
     sx={{ mt: 1 }}
     control={<Switch checked={user?.smartMoney?.isStopLossEnabled} onChange={(e, checked) => dispatch(updateLocalUser({ smartMoney: { ...user.smartMoney, isStopLossEnabled: checked } }))} name="ATR Take Profit" />}
     label="Enable ATR Stop Loss" />

    {user?.smartMoney?.isStopLossEnabled && <><Slider
      valueLabelFormat={(a, b) => `Entry candle low - (ATR * ${a})`}
      marks
      step={0.5}
      max={10}
      sx={{ mt: 6 }}
      value={user.smartMoney?.atrStop || 1.5}
      onChange={(e: any) => dispatch(updateLocalUser({ smartMoney: { ...user.smartMoney, atrStop: e.target.value } }))}
      valueLabelDisplay="on"
    />

    </> }

    <Divider />

    <FormControlLabel
     sx={{ mt: 1 }}
     control={<Switch checked={user?.smartMoney?.isTakeProfitEnabled} onChange={(e, checked) => dispatch(updateLocalUser({ smartMoney: { ...user.smartMoney, isTakeProfitEnabled: checked } }))} name="ATR Take Profit" />}
     label="Enable ATR Take Profit" />

    { user?.smartMoney?.isTakeProfitEnabled &&  <><Slider
      marks
      valueLabelFormat={(a, b) => `Entry candle high + (ATR * ${a})`}
      step={0.5}
      max={10}
      sx={{ mt: 6 }}
      value={user.smartMoney?.atrTake || 2}
      onChange={(e: any) => dispatch(updateLocalUser({ smartMoney: { ...user.smartMoney, atrTake: e.target.value } }))}
      valueLabelDisplay="on"
    /> </> }

   
   </AccordionDetails>
 </Accordion>
  )
}